import { useEffect, useState } from 'react';
import { ApiError, ConsentAccessLevel } from '@/api/generated';
import { oauthService } from '@/services/api-clients';
import { logError } from '@/utils/error-logger';
import { executeCompletionHtml } from '@/utils/sso-utils';
import {
  ACCESS_LEVEL_COPY,
  DECISION_ERROR_MESSAGE,
  LOAD_ERROR_MESSAGE,
  LOGIN_PATH,
  SESSION_ERROR_MESSAGE,
  SWITCH_ERROR_MESSAGE,
} from '../constants';
import type { AccessLevel, ConsentState, UseConsentReturn } from '../types';

export function useConsent(): UseConsentReturn {
  const [consentState, setConsentState] = useState<ConsentState>({
    status: 'loading',
  });
  const [submitting, setSubmitting] = useState<'allow' | 'deny' | null>(null);
  const [switching, setSwitching] = useState(false);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [logoFailed, setLogoFailed] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<AccessLevel>(
    ConsentAccessLevel.level.READ
  );

  useEffect(() => {
    const loadConsentInfo = async () => {
      try {
        const info = await oauthService.getConsentInfo();
        const firstLevel = info.access_levels?.find(
          level => level.level in ACCESS_LEVEL_COPY
        )?.level;

        if (firstLevel) {
          setSelectedLevel(firstLevel);
        }

        setConsentState({ status: 'ready', info });
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          setConsentState({ status: 'error', message: SESSION_ERROR_MESSAGE });

          return;
        }

        logError(error, 'consent: load info');
        setConsentState({ status: 'error', message: LOAD_ERROR_MESSAGE });
      }
    };

    void loadConsentInfo();
  }, []);

  const switchUser = async () => {
    setSwitching(true);
    setDecisionError(null);

    try {
      await oauthService.switchUser();
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setConsentState({ status: 'error', message: SESSION_ERROR_MESSAGE });
      } else {
        logError(error, 'consent: switch user');
        setSwitching(false);
        setDecisionError(SWITCH_ERROR_MESSAGE);
      }

      return;
    }

    window.location.assign(LOGIN_PATH);
  };

  const submitDecision = async (approve: boolean) => {
    setSubmitting(approve ? 'allow' : 'deny');
    setDecisionError(null);
    const accessLevel =
      consentState.status === 'ready' && consentState.info.access_levels?.length
        ? selectedLevel
        : null;
    let completionHtml: string;

    try {
      completionHtml = await oauthService.postConsent({
        approve,
        access_level: accessLevel,
      });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setConsentState({ status: 'error', message: SESSION_ERROR_MESSAGE });

        return;
      }

      logError(error, 'consent: submit decision');
      setSubmitting(null);
      setDecisionError(DECISION_ERROR_MESSAGE);

      return;
    }

    // The decision is committed server-side; a failed completion can't be
    // retried, so surface the terminal screen rather than an inline retry.
    try {
      executeCompletionHtml(completionHtml);
    } catch (error) {
      logError(error, 'consent: execute completion');
      setConsentState({ status: 'error', message: SESSION_ERROR_MESSAGE });
    }
  };

  return {
    consentState,
    submitting,
    switching,
    decisionError,
    logoFailed,
    onLogoError: () => setLogoFailed(true),
    selectedLevel,
    onSelectLevel: setSelectedLevel,
    switchUser: () => void switchUser(),
    submitDecision: (approve: boolean) => void submitDecision(approve),
  };
}
